// A form is a page's content, not a page (02-DESIGN/03-console/01-the-console.md
// § conventions: a form; hq decision 0069): *Add*, *Change*, a set-up, a
// switch, *Collect*, *Present*, *Create a workspace* and a solution's declared
// form each replace what the page or the tab held with the form, in the
// content column, under the form's own title, with the saving action and
// *Cancel*. The browser's Back closes it as *Cancel* does, and a form has no
// address: which form a page shows lives in the history entry's state, so
// opening one pushes an entry Back pops, a refresh keeps the form open, and
// nothing is in the address bar. The console has no slide-over: the component
// left the set with spec 058, and a test keeps it out.

import { useEffect, useId, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";

/** KEY is the one field of a history entry's state that names the form a page shows. */
const KEY = "form";

function record(state: unknown): Record<string, unknown> | undefined {
  return typeof state === "object" && state !== null ? (state as Record<string, unknown>) : undefined;
}

/** formOf reads which form a history entry's state names, if any. */
export function formOf(state: unknown): string | undefined {
  const v = record(state)?.[KEY];
  return typeof v === "string" && v !== "" ? v : undefined;
}

/** withForm is the entry's state with the form named and the rest of it kept — a tab a redirect chose, for one. */
export function withForm(state: unknown, form: string): Record<string, unknown> {
  return { ...(record(state) ?? {}), [KEY]: form };
}

/** withoutForm is the entry's state with no form named: the rest of it, or null where nothing else was in it. */
export function withoutForm(state: unknown): Record<string, unknown> | null {
  const r = record(state);
  if (r === undefined) {
    return null;
  }
  const rest: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(r)) {
    if (k !== KEY) {
      rest[k] = v;
    }
  }
  return Object.keys(rest).length === 0 ? null : rest;
}

export interface FormState<T extends string> {
  /** The form the page shows, or none. */
  open: T | undefined;
  /** show opens a form: a new history entry on the same address, so Back closes it. */
  show: (form: T) => void;
  /**
   * close is *Cancel*, and what a form calls when it is done: the entry loses
   * the form's name and the page is where it was. It replaces rather than
   * going back, so a page reached by a link with the form already open —
   * a refresh, a restored tab — never leaves on *Cancel*.
   */
  close: () => void;
}

/**
 * useFormState is which form a page shows, kept in the history entry's state.
 * T names the page's forms — "add" | "change" — and `open` compares to them.
 */
export function useFormState<T extends string = string>(): FormState<T> {
  const location = useLocation();
  const navigate = useNavigate();
  const here = location.pathname + location.search;
  const state: unknown = location.state;
  return {
    open: formOf(state) as T | undefined,
    show: (form) => {
      void navigate(here, { state: withForm(state, form) });
    },
    close: () => {
      void navigate(here, { replace: true, state: withoutForm(state) });
    },
  };
}

/**
 * FormPage draws a form in the page's or the tab's place: the form's own
 * title and its one line, a crumb back to what it stands in front of, the
 * fields, and the foot with *Cancel* and the saving action last. In a page's
 * place the title is the page's heading; in a tab's place, pass heading "h2".
 */
export function FormPage({
  title,
  line,
  back,
  onClose,
  foot,
  actions,
  heading = "h1",
  children,
}: {
  title: ReactNode;
  line?: ReactNode;
  /** What the form stands in front of — *People* — drawn as the crumb; a click on it is *Cancel*. */
  back?: ReactNode;
  onClose: () => void;
  /** The form's buttons: *Cancel* and the saving action, the saving one last. */
  foot?: ReactNode;
  /** A control beside the title, such as a form's *Edit as JSON*. */
  actions?: ReactNode;
  heading?: "h1" | "h2";
  children: ReactNode;
}) {
  const id = useId();
  // A form opens at the top, where its title is, as a page does.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);
  const Heading = heading;
  return (
    <section className="form-page" aria-labelledby={id}>
      {back !== undefined && (
        <div className="crumb">
          <button type="button" className="text-link" onClick={onClose}>
            {back}
          </button>
        </div>
      )}
      <header className="head form-head">
        <div>
          <Heading id={id}>{title}</Heading>
          {line !== undefined && <p className="sub">{line}</p>}
        </div>
        {actions !== undefined && <div className="head-actions">{actions}</div>}
      </header>
      <div className="form-body">{children}</div>
      {foot !== undefined && <div className="form-foot">{foot}</div>}
    </section>
  );
}
